import { Retailer } from "@/api/product-api";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/v2-components/ui/sheet";
import { Button, buttonVariants } from "@/v2-components/ui/button";
import { useMedia } from "react-use";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import Image from "next/image";
import ExternalLinkIcon from "@/v2-components/core/icon/external-link-icon";
import Link from "next/link";
import { SwitchCase } from "@/v2-components/switch-case";
import CheckIcon from "@/v2-components/core/icon/check-icon";
import WarningIcon from "@/v2-components/core/icon/warning-icon";
import { useTrans } from "@/utils/useTrans";

interface Props {
  retailers: Retailer[];
  trigger?: React.ReactNode;
}
export default function RetailerSheet({ retailers, trigger }: Props) {
  const { t } = useTrans(["tactsuit"]);
  const isMobile = useMedia("(max-width: 1023px)", false);

  return (
    <Sheet>
      <SheetTrigger asChild>
        {trigger || (
          <Button
            variant="blue-outline"
            shape="pill"
            size="sm"
            className="bg-white px-6"
          >
            {t("Find a retailer")}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent
        className="flex flex-col laptop:min-w-[800px]"
        side={isMobile ? "bottom" : "right"}
      >
        <SheetHeader className="items-center justify-between">
          <SheetTitle>{`Find a retailer`}</SheetTitle>
          <SheetDescription className="sr-only">
            {`Find a retailer`}
          </SheetDescription>
          <SheetClose className="focus:outline-none">
            <CrossIcon />
            <span className="sr-only">Close</span>
          </SheetClose>
        </SheetHeader>

        <div className="-mr-4 mt-5 overflow-auto pr-4 laptop:-mr-8 laptop:mt-9 laptop:pr-8">
          <p className="text-base text-coolgray-60">
            {`* Note: For the most up-to-date information, we recommend checking directly with your selected retailer.`}
          </p>

          <ul className="mt-7 grid gap-5 laptop:mt-9 laptop:grid-cols-2">
            {retailers.map((retailer) => (
              <li
                key={retailer.link}
                className="flex flex-col items-start gap-2"
              >
                <p className="text-lg font-600">{retailer.name}</p>
                <div className="flex h-full w-full items-center gap-3 rounded-2xl bg-coolgray-10 p-5">
                  <div className="relative h-[50px] w-[173px]">
                    <Image
                      src={retailer.imageLink}
                      alt={retailer.name}
                      className="object-cover"
                      sizes="300px"
                      fill
                    />
                  </div>

                  <div className="flex flex-col items-start gap-4">
                    <div className="flex items-center gap-1">
                      <SwitchCase
                        value={retailer.status.toLowerCase()}
                        caseBy={{
                          "in stock": (
                            <CheckIcon className="size-5 text-green-40" />
                          ),
                          backorder: (
                            <WarningIcon className="size-5 text-red-60" />
                          ),
                          "sold out": (
                            <CrossIcon className="size-5 text-red-60" />
                          ),
                        }}
                      />
                      <p className="text-sm font-600">{retailer.status}</p>
                    </div>
                    <Link
                      className={buttonVariants({
                        variant: "darkgray-fill",
                        shape: "pill",
                        size: "sm",
                        className: "pl-6 pr-4",
                      })}
                      href={retailer.link}
                      target="_blank"
                    >
                      <span>Go to Store</span>
                      <ExternalLinkIcon className="size-4.5" />
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </SheetContent>
    </Sheet>
  );
}
